import React from 'react'
import loadable from '@loadable/component'
import { isEmpty, canUseDom } from '@sainsburys-tech/boltui-utils'
import useDevice from '../MediaGallery/helpers/useDevice'
import { pushAr3dInPage } from '../../helpers/taggingSerializer'
import * as Styled from './Arkit.styles'

const ThreeViewer = loadable(() => import(/* webpackChunkName: "three-viewer" */ '../ThreeViewer/ThreeViewer'))
const AR3DButton = loadable(() => import(/* webpackChunkName: "ar-3d-button" */ './AR3DButton'))

const openARViewer = (gltf, productName) => {
  const anchor = document.createElement('a')
  const noArViewerSigil = '#no-ar'
  // eslint-disable-next-line no-restricted-globals
  const location = self.location.toString()
  const locationUrl = new URL(location)
  const modelUrl = new URL(gltf)
  const link = encodeURIComponent(location)
  const shareText = encodeURIComponent(`See ${productName} in your room!! ${location}`)
  const scheme = modelUrl.protocol.replace(':', '')

  locationUrl.hash = noArViewerSigil

  const title = encodeURIComponent(productName)

  const intent = `intent://arvr.google.com/scene-viewer/1.2?file=${gltf}&title=${title}&link=${link}&mode=ar_preferred&share_text=${shareText}#Intent;scheme=${scheme};package=com.google.android.googlequicksearchbox;action=android.intent.action.VIEW;S.browser_fallback_url=${encodeURIComponent(
    locationUrl.toString()
  )};end;`
  anchor.setAttribute('href', intent)
  anchor.click()
}

export const ArkitThumbnail = ({ gltf, productName, model, partNumber }) => {
  const isGltf = !isEmpty(gltf) && gltf.texturedModel
  const is3dModel = model || isGltf
  const { only3DViewer, isAndroidChrome, iosARCompatible } = useDevice({ partNumber, model })
  const sessionAr = (canUseDom() && sessionStorage.getItem('ar')) || false

  if (canUseDom()) pushAr3dInPage(!!is3dModel)
  if (!is3dModel) return null
  return (
    <div data-test='arkit-button'>
      {iosARCompatible || (isGltf && isAndroidChrome && !only3DViewer && !sessionAr) ? (
        <AR3DButton
          rel={iosARCompatible ? 'ar' : null}
          href={iosARCompatible ? model : null}
          onClick={!iosARCompatible ? () => openARViewer(gltf.texturedModel, productName) : null}
          data-test={iosARCompatible ? 'ar-ios' : 'ar-3d'}
          only3DViewer={only3DViewer}
          thumbnail
        />
      ) : (
        <ThreeViewer modelData={gltf} title={productName} open={only3DViewer} data-test='three-viewer' thumbnail />
      )}
    </div>
  )
}

const Arkit = ({
  model,
  gltf,
  partNumber,
  src,
  productName = '',
  isAVIFTestGroup,
  imageDimensions,
  showMediaGalleryGrid
}) => {
  const isGltf = !isEmpty(gltf) && gltf.texturedModel
  const is3dModel = model || isGltf
  const { only3DViewer, isAndroidChrome, iosARCompatible } = useDevice({ partNumber, model })
  const sessionAr = (canUseDom() && sessionStorage.getItem('ar')) || false

  if (canUseDom()) pushAr3dInPage(!!is3dModel)
  if (!is3dModel) return null
  return (
    <Styled.Container addBackground={showMediaGalleryGrid} data-test='arkit'>
      <picture>
        {isAVIFTestGroup && (
          <>
            <source
              media='(max-width: 767px)'
              type='image/avif'
              srcSet={`${imageDimensions.mobile.srcSetAvif}&fmt=avif`}
              width={imageDimensions.mobile.width}
              height={imageDimensions.mobile.height}
            />
            <source
              media='(min-width: 768px)'
              type='image/avif'
              srcSet={`${imageDimensions.desktop.srcSetAvif}&fmt=avif`}
              width={imageDimensions.desktop.width}
              height={imageDimensions.desktop.height}
            />
          </>
        )}
        <source
          media='(max-width: 767px)'
          type='image/webp'
          srcSet={`${imageDimensions.mobile.srcSet}&fmt=webp`}
          width={imageDimensions.mobile.width}
          height={imageDimensions.mobile.height}
        />
        <source
          media='(min-width: 768px)'
          type='image/webp'
          srcSet={`${imageDimensions.desktop.srcSet}&fmt=webp`}
          width={imageDimensions.desktop.width}
          height={imageDimensions.desktop.height}
        />
        <source
          media='(max-width: 767px)'
          type='image/jpeg'
          srcSet={`${imageDimensions.mobile.srcSet}&fmt=pjpeg`}
          width={imageDimensions.mobile.width}
          height={imageDimensions.mobile.height}
        />
        <source
          media='(min-width: 768px)'
          type='image/jpeg'
          srcSet={`${imageDimensions.desktop.srcSet}&fmt=pjpeg`}
          width={imageDimensions.desktop.width}
          height={imageDimensions.desktop.height}
        />
        <Styled.Image
          src={imageDimensions.desktop.src}
          width={imageDimensions.desktop.width}
          height={imageDimensions.desktop.height}
          index={src}
          role='presentation'
          loading='lazy'
        />
      </picture>
      {iosARCompatible || (isGltf && isAndroidChrome && !only3DViewer && !sessionAr) ? (
        <AR3DButton
          rel={iosARCompatible ? 'ar' : null}
          href={iosARCompatible ? model : null}
          onClick={!iosARCompatible ? () => openARViewer(gltf.texturedModel, productName) : null}
          data-test={iosARCompatible ? 'ar-ios' : 'ar-3d'}
          only3DViewer={only3DViewer}
          isFullSize={showMediaGalleryGrid}
        />
      ) : (
        <ThreeViewer
          modelData={gltf}
          title={productName}
          open={only3DViewer}
          data-test='three-viewer'
          isFullSize={showMediaGalleryGrid}
        />
      )}
    </Styled.Container>
  )
}

export default Arkit
